import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ItemProfile, ItemResource, PendingColumn, PENDING_APPROVAL_TABS, VehicleEntityName } from '@ngx-hal/models';
import { PropertiesProfilesPipe } from '@ngx-hal/pipes';
import { Observable, map, shareReplay } from 'rxjs';

export const VEHICLE_ENTITY_NAMES: readonly VehicleEntityName[] = PENDING_APPROVAL_TABS;

export interface ReferentialApprovalRequest {
  entityName: string;
  operation: 'CREATE' | 'UPDATE' | 'DELETE';
  payload: string;
  oldValue: string | null;
}

export interface RequestTracker {
  id: number;
  entityName: string;
  payload: string;
  operation: 'CREATE' | 'UPDATE' | 'DELETE';
  createdBy: string;
  createdDate: string;
  approver: string | null;
  isApproved: boolean | null;
  rejectionReason: string | null;
  oldValue: string | null;
}

export interface RequestTrackerPage {
  _embedded?: { requestTrackers?: RequestTracker[] };
  page?: { size: number; totalElements: number; totalPages: number; number: number };
}

export interface PendingApprovalTableConfig {
  columns: PendingColumn[];
  relationDisplayFields: Map<string, string>;
  nestedCreateRelationFields: Set<string>;
}

interface VehicleConfigObject {
  [key: string]: VehicleConfigValue;
}

type VehicleConfigValue = string | number | boolean | null | VehicleConfigObject | VehicleConfigValue[];

export interface ReferentialApprovalDecisionResponse {
  id: number;
  isApproved: boolean;
  approver: string;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class ReferentialApprovalRequestService {
  private readonly tableConfigRequests = new Map<string, Observable<PendingApprovalTableConfig>>();

  constructor(private readonly httpClient: HttpClient) {}

  submitReferentialApprovalRequest(request: ReferentialApprovalRequest): Observable<unknown> {
    return this.httpClient.post('/api/referential-approval-requests', request);
  }

  approveRequest(id: number): Observable<ReferentialApprovalDecisionResponse> {
    return this.httpClient.post<ReferentialApprovalDecisionResponse>(
      `/api/referential-approval-requests/${id}/approve`,
      null,
    );
  }

  rejectRequest(id: number, reason: string): Observable<ReferentialApprovalDecisionResponse> {
    return this.httpClient.post<ReferentialApprovalDecisionResponse>(
      `/api/referential-approval-requests/${id}/reject`,
      { reason },
    );
  }

  getPendingRequestTrackers(entityName: string, page: number, size: number): Observable<RequestTrackerPage> {
    return this.httpClient.get<RequestTrackerPage>('/api/requestTrackers', {
      params: { entityName: `${entityName}!!equal`, page, size, sort: 'id,desc' },
    });
  }

  getPendingApprovalTableConfig(configPath: string): Observable<PendingApprovalTableConfig> {
    const cachedRequest = this.tableConfigRequests.get(configPath);
    if (cachedRequest) return cachedRequest;

    const request = this.httpClient
      .get<{ list: Record<string, VehicleConfigObject> }>(`assets/resources-config/vehicles/${configPath}`)
      .pipe(
        map(config => this.createPendingApprovalTableConfig(config.list)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    this.tableConfigRequests.set(configPath, request);
    return request;
  }

  private createPendingApprovalTableConfig(
    config: Record<string, VehicleConfigObject>,
  ): PendingApprovalTableConfig {
    const entries = Object.entries(config);
    const columns = entries
      .filter(([, value]) => value['_hidden'] !== true)
      .sort(([, left], [, right]) => Number(left['_order'] ?? 0) - Number(right['_order'] ?? 0))
      .map(([key, value]) => ({
        key,
        title: typeof value['_title'] === 'string' ? value['_title'] : this.columnTitle(key),
      }));
    const { relationDisplayFields, nestedCreateRelationFields } = entries.reduce(
      (metadata, [key, value]) => {
        const displayField = this.approvalRelationDisplayField(value);
      if (displayField !== undefined) metadata.relationDisplayFields.set(key, displayField);
        if (value['_approvalNestedCreateRelation'] === true) metadata.nestedCreateRelationFields.add(key);
        return metadata;
      },
      {
        relationDisplayFields: new Map<string, string>(),
        nestedCreateRelationFields: new Set<string>(),
      },
    );

    return { columns, relationDisplayFields, nestedCreateRelationFields };
  }

  private approvalRelationDisplayField(config: VehicleConfigObject): string | undefined {
    const configuredField = config['_approvalDisplayField'];
    if (typeof configuredField === 'string') return configuredField;

    const fields = Object.keys(config)
      .filter(field => !field.startsWith('_'))
      .sort((left, right) => this.fieldOrder(config, left) - this.fieldOrder(config, right));
    return fields.find(field => field !== 'id') ?? fields[0];
  }

  private fieldOrder(config: VehicleConfigObject, field: string): number {
    const fieldConfig = config[field];
    if (fieldConfig === null || typeof fieldConfig !== 'object' || Array.isArray(fieldConfig)) return 0;
    return Number(fieldConfig['_order'] ?? 0);
  }

  columnTitle(key: string): string {
    const fieldName = key.split('.').slice(-1)[0];
    return fieldName
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, character => character.toUpperCase());
  }

  entityNameFromResourceName(resourceName: string): string {
    const entityName = VEHICLE_ENTITY_NAMES.find(
      tab => (tab.resourceKey ?? tab.key) === resourceName,
    )?.entityName;

    if (!entityName) {
      throw new Error(`Cannot determine referential entity name for resource '${resourceName}'.`);
    }

    return entityName;
  }

  serializeRecord<Entity extends ItemResource>(record: unknown, itemProfile: ItemProfile<Entity>): string {
    return this.serializeRecordWithRelations(record, this.relationNames(itemProfile));
  }

  private serializeRecordWithRelations(record: unknown, relationNames: string[]): string {
    const source = (record ?? {}) as Record<string, unknown>;
    const links = source['_links'];
    const recordValues = { ...source };
    delete recordValues['_embedded'];
    delete recordValues['_links'];
    const relationLinks = links && typeof links === 'object' ? links as Record<string, unknown> : {};
    const relationValues = Object.fromEntries(
      relationNames.flatMap(relationName => {
        if (source[relationName] !== undefined) {
          return [];
        }

        const href = this.hrefFromLink(relationLinks[relationName]);
        return href && this.idFromReference(href) !== null ? [[relationName, href]] : [];
      }),
    );

    return JSON.stringify({
      ...recordValues,
      ...relationValues,
      ...(links && typeof links === 'object' ? { _links: links } : {}),
    });
  }

  // Keeps oldValue's own values, but fills in any column it's missing using the
  // payload, so oldValue and payload expose the same HAL-shaped structure.
  serializeOldValue<Entity extends ItemResource>(
    oldRecord: unknown,
    payloadRecord: unknown,
    itemProfile: ItemProfile<Entity>,
  ): string {
    const relationNames = this.relationNames(itemProfile);
    const oldSource = (oldRecord ?? {}) as Record<string, unknown>;
    const payloadSource = (payloadRecord ?? {}) as Record<string, unknown>;
    const oldLinks = oldSource['_links'] as Record<string, unknown> | undefined;
    const payloadLinks = payloadSource['_links'] as Record<string, unknown> | undefined;
    const oldRelationValues = Object.fromEntries(
      relationNames.flatMap(relationName => {
        if (oldSource[relationName] !== undefined) {
          return [];
        }

        const relation = oldLinks?.[relationName];
        const href = this.hrefFromLink(relation);
        return href && this.idFromReference(href) !== null ? [[relationName, href]] : [];
      }),
    );

    const mergedRecord = {
      ...payloadSource,
      ...oldRelationValues,
      ...Object.fromEntries(Object.entries(oldSource).filter(([, value]) => value !== undefined)),
      _links: { ...payloadLinks, ...oldLinks },
    };

    return this.serializeRecordWithRelations(mergedRecord, relationNames);
  }

  // Relations are the profile's parent properties, as exposed by the shared pipe.
  private relationNames<Entity extends ItemResource>(itemProfile: ItemProfile<Entity>): string[] {
    return new PropertiesProfilesPipe()
      .transform(itemProfile, undefined, {
        remove: { children: true, functionals: true, technicals: true },
        keep: { hidden: true },
      })
      .map(propertyProfile => propertyProfile.alps.name as string);
  }

  private idFromReference(value: unknown): number | null {
    if (typeof value === 'string') {
      const id = Number(value.split('?')[0].replace(/\/$/, '').split('/').pop());
      return Number.isInteger(id) ? id : null;
    }

    if (value && typeof value === 'object') {
      const reference = value as Record<string, unknown>;
      const id = reference['id'];
      return typeof id === 'number' ? id : this.idFromReference(this.hrefFromLink(reference));
    }

    return null;
  }

  private hrefFromLink(link: unknown): string | undefined {
    if (!link || typeof link !== 'object') {
      return undefined;
    }

    const { href, _links } = link as { href?: unknown; _links?: { self?: { href?: unknown } } };
    const selfHref = _links?.self?.href;

    if (typeof selfHref === 'string') {
      return selfHref;
    }

    return typeof href === 'string' ? href : undefined;
  }

}